import * as d3 from "d3";
import { abbreviateLabel } from "./textHelper";
import { useEffect, useRef, useState } from "react";

type SimpleBarPlotProps = {
  data: { name: string; value: number }[];
};

const SimpleBarPlot = ({ data }: SimpleBarPlotProps) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect) {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
        resizeTimeout.current = setTimeout(() => {
          setDimensions({
            width: entries[0].contentRect.width,
            height: entries[0].contentRect.height,
          });
        }, 10);
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  const margin = { top: 20, right:20, bottom: 40, left: 20 };

  const width = dimensions.width;
  const height = dimensions.height;

  const xScale = d3
    .scaleBand()
    .domain(data.map((d) => d.name))
    .range([0, width - margin.left - margin.right])
    .padding(0.5);

  const yScale = d3
    .scaleLinear()
    .domain([0, 100])
    .range([height - margin.top - margin.bottom, 0]);

  const colorScale = d3
    .scaleOrdinal()
    .domain(['Autoevaluación','Líder','Colega','Subordinado','Cliente'])
    .range(['#4285f4','#ea4335','#fbbc04','#34a853','#ff6d01'])

  const gridY = yScale.ticks().map((value, i) => (
    <g key={i}>
      <line
        x1={0}
        x2={width - margin.left - margin.right}
        y1={yScale(value)}
        y2={yScale(value)}
        stroke="black"
        opacity={0.5}
      />
      <text
        x={-10}
        y={yScale(value)}
        textAnchor="middle"
        alignmentBaseline="central"
        fontSize={9}
        stroke="black"
        opacity={0.8}
      >
        {value}
      </text>
    </g>
  ));

  const shapes = data.map((d, i) => (

    <rect
      key={`Rect${i}`}
      x={xScale(d.name)}
      y={yScale(d.value)}
      width={xScale.bandwidth()}
      height={yScale(0) - yScale(d.value)}
      rx={7}
      ry={7}
      fill={colorScale(d.name) as string}
    ></rect>
  ));

  const xAxisLabels = data.map((d, i) => (
    <text
      key={`xLabel${i}`}
      x={xScale(d.name)! + xScale.bandwidth() / 2}
      y={height - margin.top - margin.bottom + 20}
      textAnchor="middle"
      alignmentBaseline="central"
      fontSize={10}
      stroke="black"
    >
      {abbreviateLabel(d.name,5)}
      <title key={`xLabel${i}Title`}>{d.name}</title>
    </text>
  ));

  return (
    <svg ref={ref} style={{ width: '100%', height: '100%' }}>
      <g
        width={width - margin.left - margin.right}
        height={height - margin.top - margin.bottom}
        transform={`translate(${[margin.left, margin.top].join(",")})`}
      >
        {gridY}
        {shapes}
        {xAxisLabels}
      </g>
    </svg>
  );
};

export default SimpleBarPlot;
